import { observer } from 'mobx-react-lite';
import {
    options_premium_StorageContent,
    options_mailboxCleanup_Used,
    options_mailboxCleanup_ofUnlimited,
    options_mailboxCleanup_Max,
    options_mailboxCleanup_AmountToUseMailbox,
    options_mailboxCleanup_storageLimitInformation,
    options_mailboxCleanup_GB,
    options_mailboxCleanup_MB,
    options_mailboxCleanup_KB,
    options_mailboxCleanup_EmptyFolderTitle,
    options_mailboxCleanup_EmptyFolderDescription,
    options_mailboxCleanup_StoragePercentageValue,
    options_mailboxCleanup_StorageUsed,
} from './StorageOptionFull.locstring.json';
import loc, { format, formatToArray } from 'owa-localize';
import * as React from 'react';
import getStore from '../store/store';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { ControlIcons } from 'owa-control-icons';
import StorageRow from './StorageRow';
import { isFeatureEnabled } from 'owa-feature-flags';
import BaseFolderType from 'owa-service/lib/contract/BaseFolderType';
import { default as storageStore } from 'owa-storage-store/lib/store/store';
import StorageUsage from './StorageUsage';

const styles = require('./StorageOptionFull.scss');
const classNames = require('classnames/bind').bind(styles);

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

export interface StorageOptionFullProps {
    isPremium: boolean;
}

export default observer(function StorageOptionFull(props: StorageOptionFullProps) {
    const { isPremium } = props;
    const isMailboxCleanupShown = isFeatureEnabled('tri-mailboxCleanup');

    const { sendQuota, usageInBytes, usagePercentage, sendReceiveQuota } = storageStore();

    const { folders } = getStore();

    const isExceededQuota = !sendQuota.IsUnlimited && usageInBytes >= sendQuota.Value;
    const progressBarStyle = { width: usagePercentage + '%' };
    let storageUsedString;
    const storageLimitInfo = format(
        loc(options_mailboxCleanup_storageLimitInformation),
        (sendReceiveQuota.Value / GB).toFixed(2)
    );

    const storageUsedWithPercentage = formatToArray(
        loc(options_mailboxCleanup_StoragePercentageValue),
        (usageInBytes / GB).toFixed(2),
        (sendReceiveQuota.Value / GB).toFixed(2),
        <span className={styles.usagePercentageText}>{usagePercentage.toFixed(1)}%</span>
    );

    // Premium option has different description text
    if (isPremium) {
        storageUsedString = format(
            loc(options_premium_StorageContent),
            (sendReceiveQuota.Value / GB).toFixed(2),
            (usageInBytes / GB).toFixed(2)
        );
    } else {
        const amountUsed = getFormattedTextBasedOnSize(
            usageInBytes,
            loc(options_mailboxCleanup_Used)
        );
        const maxAmount = sendReceiveQuota.IsUnlimited
            ? loc(options_mailboxCleanup_ofUnlimited)
            : getFormattedTextBasedOnSize(sendReceiveQuota.Value, loc(options_mailboxCleanup_Max));

        storageUsedString = formatToArray(
            loc(options_mailboxCleanup_StorageUsed),
            amountUsed,
            maxAmount
        );
    }

    return (
        <>
            <div className={styles.storageLimitInformationContainer}>{storageLimitInfo}</div>
            {isMailboxCleanupShown ? (
                <div
                    className={classNames(
                        styles.progressText,
                        usagePercentage >= 90 && styles.usagePercentageFull
                    )}>
                    {storageUsedWithPercentage}
                </div>
            ) : (
                <div className={styles.progressText}>{storageUsedString}</div>
            )}

            {isMailboxCleanupShown ? (
                <StorageUsage isPremium={isPremium} />
            ) : (
                <div className={styles.usageContainer}>
                    <div
                        className={classNames(
                            styles.mailboxStorageUsed,
                            isExceededQuota && styles.exceededQuota
                        )}
                        style={progressBarStyle}
                    />
                </div>
            )}
            {isExceededQuota && !isMailboxCleanupShown && (
                <div className={styles.warningText}>
                    <div
                        className={classNames(getIconClassName(ControlIcons.Warning), styles.icon)}
                    />
                    <span>
                        {getFormattedTextBasedOnSize(
                            usageInBytes - sendQuota.Value,
                            loc(options_mailboxCleanup_AmountToUseMailbox)
                        )}
                    </span>
                </div>
            )}
            {isMailboxCleanupShown && folders.length > 0 && (
                <div className={styles.folderStorageInformation}>
                    <p className={styles.folderStorageTitle}>
                        {loc(options_mailboxCleanup_EmptyFolderTitle)}
                    </p>
                    <p className={styles.folderStorageText}>
                        {loc(options_mailboxCleanup_EmptyFolderDescription)}
                    </p>
                    {renderCleanupFolders(folders)}
                </div>
            )}
        </>
    );
});

function renderCleanupFolders(folders: BaseFolderType[]): JSX.Element[] {
    let storageRows = [];
    for (let i = 0; i < folders.length; i++) {
        let currentFolder = folders[i];
        storageRows.push(
            <StorageRow
                folder={currentFolder}
                usedSpace={getFormattedTextBasedOnSize(
                    currentFolder.ExtendedProperty[0].Value,
                    loc(options_mailboxCleanup_Used)
                )}
                key={currentFolder.FolderId.Id}
            />
        );
    }
    return storageRows;
}

const getFormattedTextBasedOnSize = (sizeInBytes: number, stringToFormat: string): string => {
    const numberFormat = sizeInBytes >= GB ? GB : sizeInBytes >= MB ? MB : KB;
    const numberUnitString =
        sizeInBytes >= GB
            ? loc(options_mailboxCleanup_GB)
            : sizeInBytes >= MB
            ? loc(options_mailboxCleanup_MB)
            : loc(options_mailboxCleanup_KB);
    return format(stringToFormat, (sizeInBytes / numberFormat).toFixed(2), numberUnitString);
};
